import { useCallback, useEffect, useState } from 'react';
import '@/styles/battlepass-layout.css';
import { useLocale } from '@/context/LocaleContext';
import { assetUrl } from '@/lib/assetUrl';
import {
  CHINESE_FIXED_SKILLS,
  CHINESE_WEAPONS,
  EUROPEAN_SKILLS,
  getDefaultChineseSkills,
  type RegisterRace,
  type RegisterSkillOption,
  type RegisterSkillsSelection,
} from '@/lib/registerSkillsConfig';

interface RegisterSkillsModalProps {
  open: boolean;
  race: RegisterRace;
  onClose: () => void;
  onConfirm: (skills: RegisterSkillsSelection) => void;
}

function skillImg(option: RegisterSkillOption, selected: boolean): string {
  return assetUrl(selected ? option.focusImg : option.img);
}

export function RegisterSkillsModal({ open, race, onClose, onConfirm }: RegisterSkillsModalProps) {
  const { t } = useLocale();
  const [selectedSkills, setSelectedSkills] = useState<RegisterSkillsSelection>({});
  const [errorSections, setErrorSections] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open) return;
    if (race === 'chinese') {
      setSelectedSkills(getDefaultChineseSkills());
    } else {
      setSelectedSkills({});
    }
    setErrorSections(new Set());
  }, [open, race]);

  const selectSkill = useCallback((option: RegisterSkillOption) => {
    if (option.fixed) return;
    setSelectedSkills((prev) => ({ ...prev, [option.type]: option.skill }));
  }, []);

  const flashSectionError = useCallback((sectionKey: string) => {
    setErrorSections((prev) => new Set(prev).add(sectionKey));
    window.setTimeout(() => {
      setErrorSections((prev) => {
        const next = new Set(prev);
        next.delete(sectionKey);
        return next;
      });
    }, 400);
  }, []);

  const handleConfirm = () => {
    if (race === 'european') {
      let hasError = false;
      if (!selectedSkills.weapon) {
        flashSectionError('eu-weapons');
        hasError = true;
      }
      if (!selectedSkills.buff) {
        flashSectionError('eu-buffs');
        hasError = true;
      }
      if (hasError) return;
    } else {
      if (!selectedSkills.weapon) {
        flashSectionError('cn-weapons');
        return;
      }
    }

    onConfirm({
      ...selectedSkills,
      ...(race === 'chinese' ? getDefaultChineseSkills() : {}),
    });
    onClose();
  };

  if (!open) return null;

  const renderSelectable = (option: RegisterSkillOption) => {
    const selected = selectedSkills[option.type] === option.skill;
    return (
      <button
        type="button"
        key={`${option.type}-${option.skill}`}
        className={`skill-icon${selected ? ' selected' : ''}${option.fixed ? ' skill-icon-fixed selected' : ''}`}
        data-skill={option.skill}
        data-type={option.type}
        onClick={() => selectSkill(option)}
        aria-label={option.label}
        aria-pressed={selected || Boolean(option.fixed)}
        disabled={option.fixed}
      >
        <img src={skillImg(option, selected || Boolean(option.fixed))} alt={option.label} className="skill-img" />
        {option.fixed ? <span className="skill-badge-auto">AUTO</span> : null}
        {!option.fixed ? (
          <span className="skill-check">
            <i className="ph-fill ph-check-circle" />
          </span>
        ) : null}
      </button>
    );
  };

  return (
    <div id="skills-modal" className="skills-modal silk-modal--aaa" role="dialog" aria-modal="true" aria-labelledby="register-skills-title">
      <div className="skills-modal-overlay battlepass-exp-modal-overlay" onClick={onClose} aria-hidden />
      <div className="skills-modal-content reset-spin-modal-content">
        <div className="skills-modal-header reset-spin-modal-header">
          <h3 id="register-skills-title" className="skills-modal-title">{t('reg_modal_title')}</h3>
          <button type="button" className="skills-modal-close battlepass-exp-modal-close" aria-label={t('silk_confirm_close')} onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="skills-modal-body reset-spin-modal-body">
          {race === 'european' ? (
            <div id="skills-european">
              <div
                className={`skills-section${errorSections.has('eu-weapons') ? ' skills-section-error' : ''}`}
              >
                <h4 className="skills-section-title">{t('reg_weapons')}</h4>
                <div className="skills-grid">
                  {EUROPEAN_SKILLS.filter((s) => s.type === 'weapon').map(renderSelectable)}
                </div>
              </div>
              <div className={`skills-section${errorSections.has('eu-buffs') ? ' skills-section-error' : ''}`}>
                <h4 className="skills-section-title">{t('reg_buffs')}</h4>
                <div className="skills-grid">
                  {EUROPEAN_SKILLS.filter((s) => s.type === 'buff').map(renderSelectable)}
                </div>
              </div>
            </div>
          ) : (
            <div id="skills-chinese">
              <div
                className={`skills-section${errorSections.has('cn-weapons') ? ' skills-section-error' : ''}`}
              >
                <h4 className="skills-section-title">{t('reg_weapons')}</h4>
                <div className="skills-grid">{CHINESE_WEAPONS.map(renderSelectable)}</div>
              </div>
              <div className="skills-section">
                <h4 className="skills-section-title">{t('reg_essence')}</h4>
                <div className="skills-grid">
                  {CHINESE_FIXED_SKILLS.filter((s) => s.type === 'essence').map(renderSelectable)}
                </div>
              </div>
              <div className="skills-section">
                <h4 className="skills-section-title">{t('reg_force')}</h4>
                <div className="skills-grid">
                  {CHINESE_FIXED_SKILLS.filter((s) => s.type === 'force').map(renderSelectable)}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="skills-modal-footer reset-spin-modal-footer">
          <button type="button" className="reset-spin-btn-cancel" onClick={onClose}>
            {t('reg_btn_cancel')}
          </button>
          <button type="button" className="reset-spin-btn-confirm" onClick={handleConfirm}>
            {t('reg_btn_register')}
          </button>
        </div>
      </div>
    </div>
  );
}
